import { useState } from 'react';
import { toast } from 'sonner';
import { Copy, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { IssuedWellToken } from '../types';

// The ingest endpoint the device will POST to. Mirrors the curl printed by
// scripts/mint-well-token.ts so the person configuring the device gets the
// endpoint + header shape, not just the secret. Resolved to an ABSOLUTE URL so
// the example is directly runnable: VITE_API_URL may be absolute
// (http://host:3001) or a proxy-relative path (/api) — new URL() handles both.
const INGEST_URL = new URL(
  `${import.meta.env.VITE_API_URL ?? ''}/ingest/readings`,
  window.location.origin,
).href;

function curlExample(token: string): string {
  return [
    `curl -sS -X POST ${INGEST_URL} \\`,
    `  -H "Authorization: Bearer ${token}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"temperature":21.4,"pressure":1013,"qAnalytics":0.87}'`,
  ].join('\n');
}

interface Props {
  issued: IssuedWellToken | null;
  onClose: () => void;
}

// Shows a freshly-issued device token EXACTLY ONCE. The raw token lives only in
// the `issued` prop held by the parent; closing this dialog clears it there, so
// it never persists past acknowledgement. An explicit "I've saved this"
// checkbox gates the close, because the token cannot be retrieved again.
export function TokenShowOnceDialog({ issued, onClose }: Props) {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setAcknowledged(false);
      onClose();
    }
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Clipboard unavailable — copy manually');
    }
  };

  return (
    <Dialog open={issued !== null} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="token-show-once-dialog">
        <DialogHeader>
          <DialogTitle>{issued?.rotated ? 'Token rotated' : 'Device token issued'}</DialogTitle>
          <DialogDescription>
            {issued?.rotated
              ? 'The previous token has been revoked. The device must be reconfigured with the new token below.'
              : 'Copy this token now and store it securely.'}
          </DialogDescription>
        </DialogHeader>

        {issued && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>
                This is the only time this token will be shown. It cannot be retrieved again — if
                lost, you must rotate to issue a new one.
              </span>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">
                Token ({issued.wellCode})
              </div>
              <div className="flex items-center gap-2">
                <code
                  data-testid="token-show-once-value"
                  className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 font-mono text-sm"
                >
                  {issued.token}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="token-show-once-copy"
                  onClick={() => void copy(issued.token, 'Token')}
                >
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Example request</div>
              <pre className="overflow-x-auto rounded bg-muted p-2 font-mono text-xs">
                {curlExample(issued.token)}
              </pre>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="px-0"
                onClick={() => void copy(curlExample(issued.token), 'Example request')}
              >
                <Copy className="mr-1 h-3.5 w-3.5" /> Copy example
              </Button>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="token-show-once-ack"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              I have saved this token securely
            </label>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            data-testid="token-show-once-done"
            disabled={!acknowledged}
            onClick={() => handleOpenChange(false)}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
